import { FC, useContext, useEffect } from 'react'
import PageContainer from 'shared/components/PageContainer'
import Background from 'components/Background'
import { Proto } from 'types/demo3'
import jsLogger from 'js-logger'
import Conversation from 'pages/Conversation'
import StreamDataProvider, {
  PermissionErrorType
} from 'shared/contexts/StreamDataProvider'
import { BackgroundType } from '@facesignai/api'
import { getBackClient } from 'shared/controllers/back'
import ConnnectionContext from 'shared/contexts/ConnectionContext'
import ConnectionStatus from 'shared/components/ConnectionStatus'
import NetworkSpeedCheck from 'shared/components/NetworkSpeedCheck'
import { useGetSet } from 'react-use'

// Server-authoritative scheme: the socket server owns STT, turn-taking,
// barge-in, phrase generation and avatar provisioning + voicing. This page only
// provisions the media stream, keeps client-side user-video recording wired
// (StreamDataProvider.sendVideoChunk → socket), and reflects the session
// lifecycle the server relays (sessionFinished → recap). No client-side turn
// loop, no HTTP get_session_state / apply_response polling.

type Props = {
  sessionInfo: Proto.ConversationStartResponse
  onComplete: () => void
  started: boolean
  onError: (error: string) => void
  onPermissionsError: (permissionsError: PermissionErrorType) => void
  onIdleTimeout?: () => void
}

const Session: FC<Props> = ({
  sessionInfo,
  onComplete,
  onError,
  started,
  onPermissionsError,
  onIdleTimeout
}) => {
  const { updateConnectionStatus } = useContext(ConnnectionContext)
  const [getComplete, setComplete] = useGetSet(false)

  useEffect(() => {
    const back = getBackClient()
    back.connectionStatusHandler = (isConnected: boolean) =>
      updateConnectionStatus('back', isConnected)

    // Explicit end-of-session signal from the server (fires when the final
    // isOver phrase finishes playing) → go straight to recap. This is the sole
    // recap trigger; the avatar's speaking lifecycle is owned by the server.
    back.sessionFinishedHandler = () => {
      jsLogger.log('Session: sessionFinished → complete')
      setComplete(true)
      onComplete()
    }

    return () => {
      jsLogger.log('Session: disconnect back on unmounting')
      const back = getBackClient()
      back.disconnect()
    }
  }, [])

  const handleChunk = (
    videoBlob: Blob,
    mimeType: string,
    role: 'avatar' | 'user'
  ) => {
    getBackClient().sendVideoChunk(videoBlob, mimeType, role)
  }

  const renderBackground = () => {
    return (
      <Background
        customization={{ backgroundType: BackgroundType.AVATAR }}
        avatarImageUrl={sessionInfo.sessionSettings.avatarImageUrl}
      />
    )
  }

  return (
    <PageContainer>
      {renderBackground()}
      <div
        className={`absolute top-0 left-0 bottom-0 right-0 w-full h-full flex items-center justify-center`}
      >
        <StreamDataProvider
          onError={onError}
          onPermissionsError={onPermissionsError}
          isLazy={!started}
          sendVideoChunk={handleChunk}
        >
          <Conversation
            sessionInfo={sessionInfo}
            started={started}
            onError={onError}
            onIdleTimeout={onIdleTimeout}
          />
        </StreamDataProvider>
      </div>
      {!getComplete() && <ConnectionStatus />}
      {!getComplete() && <NetworkSpeedCheck />}
    </PageContainer>
  )
}

export default Session
